// components/ViewContractModal.js
import React, { useState, useEffect, useRef } from "react";
import { FiX, FiBriefcase, FiEye, FiDownload } from "react-icons/fi";
import { FileText, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import moment from "moment";

const FileItem = ({ file, onView }) => {
  const getFileTypeColor = (fileName) => {
    if (!fileName || typeof fileName !== "string") {
      return "text-gray-600 bg-gray-50";
    }
    const ext = fileName.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "text-red-600 bg-red-50";
      case "docx":
      case "doc":
        return "text-blue-600 bg-blue-50";
      case "xlsx":
      case "xls":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-300">
      <div className="flex items-center p-4 space-x-4">
        <div className={`flex-shrink-0 p-3 rounded-lg transition-colors ${getFileTypeColor(file.originalName)}`}>
          <FileText className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate pr-2">
            {file.originalName}
          </p>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-xs font-medium text-green-600">Contract Document</span>
            <span className="text-xs text-gray-500">
              {moment(file.uploadedAt || file.createdAt).format("MMM DD, YYYY")}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onView && onView(file)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <FiEye className="w-4 h-4 mr-1" />
            View
          </button>
          <button
            type="button"
            onClick={() => onView && onView(file)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all duration-200"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ViewContractModal({ isOpen, onClose, contract }) {
  const modalRef = useRef(null);
  const [contractDocuments, setContractDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Fetch contract documents when modal opens
  useEffect(() => {
    if (isOpen && contract) {
      fetchContractDocuments();
    }
  }, [isOpen, contract]);

  const fetchContractDocuments = async () => {
    setLoading(true);
    try {
      // Replace with your actual API call to get contract documents
      const token = localStorage.getItem("token");
      const response = await fetch(`"https://ims.holdthechild.net"/api/staff/${contract.id}/documents`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setContractDocuments(data.documents || []);
      }
    } catch (error) {
      console.error("Error fetching contract documents:", error);
      toast.error("Failed to load contract documents");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    } else {
      toast.error("Document URL not available");
    }
  };

  if (!isOpen || !contract) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative transform transition-all duration-300 ease-out max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiBriefcase className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-black">
                  Contract Details: <span className="text-gray-900">{contract.name}</span>
                </h2>
                <div className="flex items-center mt-1 text-black text-sm">
                  <FiBriefcase className="w-3 h-3 mr-1" />
                  <span>Contract Management</span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-4 sm:p-6">
          {/* Contract Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-lg font-semibold text-gray-900">{contract.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Job Title</label>
                <p className="text-gray-900">{contract.title}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Department</label>
                <p className="text-gray-900">{contract.department}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Contract Type</label>
                <p className="text-gray-900">{contract.type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Expiry Date</label>
                <p className="text-gray-900">{moment(contract.expiryDate).format("MMMM DD, YYYY")}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  contract.status === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {contract.status}
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          {/* Contract Documents - VIEW ONLY */}
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-orange-500" />
                Contract Documents
                {contractDocuments.length > 0 && (
                  <span className="ml-2 inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    {contractDocuments.length} document(s)
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 mt-1">View and download uploaded contract documents</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : contractDocuments.length > 0 ? (
              <div className="space-y-3">
                {contractDocuments.map((file, index) => (
                  <FileItem
                    key={file._id || index}
                    file={file}
                    onView={handleViewDocument}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-1">No contract documents found</p>
                <p className="text-sm text-gray-500">No documents have been uploaded for this contract yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200">
          <button 
            onClick={onClose} 
            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
